import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@fp/ui/components/base/buttons/button";
import { OrganizationAvatar } from "@fp/ui/components/base/avatar/organization-avatar";
import {
  Dialog,
  Modal,
  ModalOverlay,
} from "@fp/ui/components/application/modals/modal";
import { Heading } from "react-aria-components";
import { X, Check, Plus } from "@untitledui/icons";
import { cx } from "@fp/ui/utils/cx";
import { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { useUserContext, type UserOrganization } from "./user-context";

interface OrgSwitcherModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OrgSwitcherModal({ isOpen, onClose }: OrgSwitcherModalProps) {
  const { organizations, orgId, setOrgId } = useUserContext();
  const navigate = useNavigate();
  const [isSwitching, setIsSwitching] = useState<string | null>(null);

  const sortedOrgs = [...organizations].sort((a, b) =>
    a.organizationName.localeCompare(b.organizationName),
  );

  const handleOrgSelect = async (org: UserOrganization) => {
    if (org.organizationId === orgId || isSwitching) return;

    setIsSwitching(org.organizationId);
    try {
      await setOrgId(org.organizationId);
      onClose();
    } catch (err) {
      toast.error(
        getErrorMessage(err, "Failed to switch organizations. Please try again."),
      );
    } finally {
      setIsSwitching(null);
    }
  };

  const handleManageOrgs = () => {
    navigate({ to: "/organizations" });
    onClose();
  };

  const handleCreateOrJoin = () => {
    navigate({ to: "/onboarding", search: { add: true } });
    onClose();
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open: boolean) => !open && onClose()}
      className="items-center"
    >
      <Modal className="max-w-sm">
        <Dialog>
          <div className="bg-white rounded-lg w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <Heading slot="title" level={3} className="text-lg font-semibold">
            Switch Organization
          </Heading>
              <Button color="tertiary" size="sm" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 py-2">
          {sortedOrgs.map((org) => {
            const isCurrent = org.organizationId === orgId;
            const isLoading = isSwitching === org.organizationId;

            return (
              <button
                key={org.organizationId}
                type="button"
                disabled={isLoading || (isSwitching !== null && !isLoading)}
                onClick={() => handleOrgSelect(org)}
                className={cx(
                  "flex items-center gap-3 px-4 py-3 w-full text-left",
                  !isSwitching && "hover:bg-gray-50 cursor-pointer",
                  isSwitching && !isLoading && "opacity-50 cursor-not-allowed",
                  isCurrent && "bg-gray-50",
                )}
              >
                <OrganizationAvatar
                  name={org.organizationName}
                  logoUrl={org.logoUrl}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {org.organizationName}
                  </div>
                </div>
                {isCurrent && (
                  <Check className="size-5 text-green-600 shrink-0" />
                )}
                {isLoading && (
                  <div className="size-5 shrink-0 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                )}
              </button>
            );
          })}
        </div>

        <div className="border-t border-gray-200 p-3 space-y-2">
          <Button
            color="primary"
            className="w-full"
                onClick={handleManageOrgs}
          >
            Manage organizations
          </Button>
          <Button
            color="tertiary"
            className="w-full"
                onClick={handleCreateOrJoin}
            iconLeading={Plus}
          >
            Create or join organization
          </Button>
        </div>
      </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
